import axios from 'axios';

// eslint-disable-next-line no-undef
const baseUrl = process.env.REACT_APP_API_URL;

let cancelTokenSource;

export function cancelApiRequest() {
	if (cancelTokenSource) {
		cancelTokenSource.cancel();
	}
}

export async function sendNoteToEnhance(data) {
	try {
		const response = await axios.post(`${baseUrl}/note_enhancement`, data);
		return response.data;
	} catch (error) {
		if (error.response) {
			return error.response.data;
		} else {
			console.error('Something went wrong', error.message);
			return { message: error.message };
		}
	}
}

export const generateCarePlan = async (
	token,
	bodyData,
	updateOptimizedPlan,
) => {
	try {
		const response = await fetch(
			`${baseUrl}/feedback_whole_plan_generation`,
			{
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(bodyData),
			},
		);

		if (!response.body) throw new Error('No stream body');

		const reader = response.body.getReader();
		const decoder = new TextDecoder('utf-8');

		let buffer = '';
		let done = false;

		while (!done) {
			const result = await reader.read();
			done = result.done;

			if (done) {
				if (buffer) {
					processBufferForOP(buffer, updateOptimizedPlan);
				}
				break;
			}

			const value = result.value;
			buffer += decoder.decode(value, { stream: true });
			buffer = processBufferForOP(buffer, updateOptimizedPlan);
		}
	} catch (error) {
		console.error('Error fetching care plan:', error);
	}
};

/***
 * This is a stream api and normal api of optimized care plan generation
 */
export async function fetchCurrentPlan(
	data,
	isEnableStreaming = false,
	updateOptimizedPlan,
) {
	try {
		if (isEnableStreaming) {
			const response = await fetch(`${baseUrl}/api/care-plan/process`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(data),
			});

			const reader = response.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let jsonBuffer = '';

			const handleStream = async () => {
				// eslint-disable-next-line no-constant-condition
				while (true) {
					const { done, value } = await reader.read();
					if (done) {
						processRemainingBuffer(jsonBuffer);
						break;
					}
					jsonBuffer += decoder.decode(value, { stream: true });
					jsonBuffer = processBuffer(jsonBuffer, updateOptimizedPlan);
				}
			};

			handleStream();
		} else {
			if (cancelTokenSource) {
				cancelTokenSource.cancel();
			}
			cancelTokenSource = axios.CancelToken.source();

			const response = await axios.post(
				`${baseUrl}/api/care-plan/process`,
				data,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
					},
					cancelToken: cancelTokenSource.token,
				},
			);
			return response.data;
		}
	} catch (error) {
		if (axios.isCancel(error)) {
			return { error: true, canceled: true };
		}
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function addCarePlanTemplate(data) {
	try {
		const response = await axios.post(
			`${baseUrl}/upload_instruction`,
			data,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			},
		);
		return { error: false, data: response.data };
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function addFeedBackToPlan(data) {
	try {
		const response = await axios.post(
			`${baseUrl}/feedback_plan_generation`,
			data,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function getCarePlanTemplate() {
	try {
		const response = await axios.get(`${baseUrl}/read_instruction`);
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function updateCarePlanTemplate(data) {
	try {
		const response = await axios.post(
			`${baseUrl}/update_instruction`,
			data,
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function connectWithApi(apiUrl, apiToken, residentId) {
	try {
		const config = {
			headers: {
				Authorization: 'Bearer ' + apiToken,
			},
		};
		const response = await axios.post(`${apiUrl}/${residentId}`, config);
		return response.data;
	} catch (error) {
		if (error.response) {
			return error.response.data;
		} else {
			console.error('Something went wrong', error.message);
			return { message: error.message };
		}
	}
}

export async function uploadInitialCarePlan(data) {
	try {
		if (cancelTokenSource) {
			cancelTokenSource.cancel();
		}
		cancelTokenSource = axios.CancelToken.source();

		const response = await axios.post(`${baseUrl}/upload_files`, data, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
			cancelToken: cancelTokenSource.token,
		});
		return response.data;
	} catch (error) {
		if (axios.isCancel(error)) {
			return { error: true, canceled: true };
		}
		if (error.response) {
			return {
				error: true,
				message: error.response.data.error,
				data: error.response.data,
			};
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function generateInitialCareGoals(data) {
	try {
		if (cancelTokenSource) {
			cancelTokenSource.cancel();
		}
		cancelTokenSource = axios.CancelToken.source();

		const response = await axios.post(
			`${baseUrl}/generate-initial-care-goals`,
			data,
			{ cancelToken: cancelTokenSource.token },
		);
		return response.data;
	} catch (error) {
		if (axios.isCancel(error)) {
			return { error: true, canceled: true };
		}
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function sendClientApiDetails(data) {
	try {
		const response = await axios.post(
			`${baseUrl}/register_api_provider`,
			data,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function extractTemplate(data, route) {
	try {
		const response = await axios.post(`${baseUrl}/${route}`, data, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function getPlanValidation(body) {
	try {
		const response = await axios.post(
			`${baseUrl}/care-plans/validate`,
			body,
			{
				headers: {
					'Content-Type': 'application/json',
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

/***
 * This is a stream api of initial care plan generation
 */
export const generateInitialCarePlan = async (bodyData, updateInitialPlan) => {
	try {
		const response = await fetch(`${baseUrl}/generate-initial-care-plan`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(bodyData),
		});

		const reader = response.body.getReader();
		const decoder = new TextDecoder('utf-8');
		let jsonBuffer = '';

		const handleStream = async () => {
			// eslint-disable-next-line no-constant-condition
			while (true) {
				const { done, value } = await reader.read();
				if (done) {
					processRemainingBuffer(jsonBuffer);
					break;
				}
				jsonBuffer += decoder.decode(value, { stream: true });
				jsonBuffer = processBuffer(jsonBuffer, updateInitialPlan);
			}
		};

		handleStream();
	} catch (error) {
		console.error('Error fetching meal plan:', error);
	}
};

export async function fetchAdmissionAssessmentData(data) {
	try {
		const response = await axios.post(
			`${baseUrl}/api/assess/extract`,
			data,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function generateAdmissionAssessment(data) {
	try {
		const response = await axios.post(
			`${baseUrl}/api/assess/estimatedcost-generate`,
			data,
			{
				headers: {
					'Content-Type': 'application/json',
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function fetchAdmissionAssessmentPdfUrl(data) {
	try {
		const response = await axios.post(
			`${baseUrl}/api/assess/estimatedcost-pdf`,
			data,
			{
				headers: {
					'Content-Type': 'application/json',
				},
				responseType: 'blob',
			},
		);

		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

export async function onExtractCQCTemplate(data) {
	try {
		const response = await axios.post(
			`${baseUrl}/cqc_notifications/onboarding/extract`,
			data,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			return { error: true, message: error.response.data.error };
		} else {
			console.error('Something went wrong', error.message);
			return { error: true, message: error.message };
		}
	}
}

const processBuffer = (buffer, updatePlan) => {
	let boundary;
	while ((boundary = buffer.indexOf('\n')) !== -1) {
		const jsonString = buffer.slice(0, boundary);
		buffer = buffer.slice(boundary + 1); // Update the buffer before parsing to ensure complete data is removed even if parsing fails.
		if (jsonString.trim()) {
			if (jsonString.startsWith('event')) {
				continue;
			}

			// Only process non-empty lines
			try {
				const newJson = jsonString.replace('data: ', '');
				const plan = JSON.parse(newJson);
				updatePlan(plan);
			} catch (error) {
				console.error('Error parsing JSON:', error);
			}
		}
	}
	return buffer;
};

const processRemainingBuffer = (buffer, updatePlan) => {
	if (buffer.trim()) {
		// Only process non-empty remaining buffer
		try {
			const newJson = buffer.replace('data: ', '');
			const plan = JSON.parse(newJson);
			updatePlan(plan);
		} catch (error) {
			console.error('Error parsing remaining JSON:', error);
		}
	}
};

const processBufferForOP = (buffer, updatePlan) => {
	const events = buffer.split('\n\n'); // SSE events are separated by blank line
	buffer = events.pop(); // keep incomplete chunk

	for (const eventBlock of events) {
		const lines = eventBlock.split('\n');

		let eventType = null;
		let dataStr = '';

		for (const line of lines) {
			if (line.startsWith('event:')) {
				eventType = line.replace('event:', '').trim();
			}

			if (line.startsWith('data:')) {
				dataStr += line.replace('data:', '').trim();
			}
		}

		if (!dataStr) continue;

		try {
			const data = JSON.parse(dataStr);

			updatePlan({
				event: eventType,
				data,
			});
		} catch (err) {
			console.error('SSE JSON parse error:', err, dataStr);
		}
	}

	return buffer;
};
